import { DicomMessageLevel, type DicomSeries } from '@/lib/dicom/DicomSeries';
import { logger } from '@/util/logger';
import type { DicomInfo } from '@/lib/dicom/DicomInfo';
import { DicomInfoUtil } from '@/lib/dicom/DicomInfoUtil';
import { isFiniteNumber } from '@/util/isFiniteNumber';
import { filterExcluded } from '@/lib/dicom/dicomFileChecks';
import { DicomMessages } from '@/lib/dicom/dicomMessages';

/**
 * Series-level geometry checks: pixel spacing, image orientation, slice spacing
 * and contiguity, slice thickness, scan coverage and slice ordering.
 */

const log = logger('DicomSeriesGeometryChecks');

const SLICE_DISTANCE_TOLERANCE = 0.1; // mm
const MIN_SLICE_SPACING = 0.25; // mm
const MAX_SLICE_SPACING = 1.6; // mm

const MIN_SCAN_LENGTH = 200.0; // mm

const MAX_PIXEL_SPACING = 3.0; // mm
const PIXEL_SPACING_WARNING = 2.0; // mm

const MAX_THICKNESS = 3.0; // mm
const THICKNESS_WARNING = 2.0; // mm

export class DicomSeriesGeometryChecks {
    /**
     * Enumerate all files that haven't been excluded and check that
     *   - the file has pixel spacing
     *   - the pixel spacing is less than MAX_PIXEL_SPACING
     * If either x or y is > MAX_PIXEL_SPACING then mark the series as excluded.
     * If any file has pixel spacing greater than PIXEL_SPACING_WARNING, then add a warning to that file.
     */
    static checkPixelSpacing(group: DicomSeries): void {
        if (
            !filterExcluded(group).every((i) => {
                if (i.pixelSpacing && i.pixelSpacing.length === 2) {
                    const [x, y] = i.pixelSpacing;
                    if (x !== undefined && y !== undefined) {
                        if (x > MAX_PIXEL_SPACING || y > MAX_PIXEL_SPACING) {
                            return false;
                        }

                        if (x > PIXEL_SPACING_WARNING || y > PIXEL_SPACING_WARNING) {
                            DicomMessages.appendMessage(
                                i.messages,
                                DicomMessageLevel.Warning,
                                `The pixel spacing is greater than ${PIXEL_SPACING_WARNING}mm`,
                            );
                        }
                        return true;
                    }
                }
                return false;
            })
        ) {
            DicomMessages.excludeWithInfoMessage(
                group,
                `Pixel spacing must be less than ${MAX_PIXEL_SPACING}mm`,
            );
        }
    }

    /**
     * Check that every image in the series has the correct patient orientation. The
     * orientation is expressed as a vector of 6 numbers (3 positions, 3 cosines).
     *
     * @see {@link https://dicom.innolitics.com/ciods/ct-image/image-plane/00200032}
     */
    static checkImageOrientationPatient(group: DicomSeries): void {
        if (
            !filterExcluded(group).every((i) => {
                if (DicomSeriesGeometryChecks.isIdentityOrientation(i.imageOrientationPatient)) {
                    return true;
                }
                log.info(
                    "File '%s' image orientation patient %o",
                    i.file.name,
                    i.imageOrientationPatient,
                );
                return false;
            })
        ) {
            DicomMessages.excludeWithInfoMessage(
                group,
                `The image orientation patient must be [1.0, 0.0, 0.0, 0.0, 1.0, 0.0]`,
            );
        }
    }

    private static isIdentityOrientation(values: Array<number | undefined> | undefined): boolean {
        if (!values || values.length !== 6) {
            return false;
        }
        const expected = [1.0, 0.0, 0.0, 0.0, 1.0, 0.0];
        return values.every(
            (v, idx) =>
                v !== undefined &&
                DicomSeriesGeometryChecks.areEqualWithinTolerance(v, expected[idx], 0.001),
        );
    }

    /**
     * Check that the slice spacing (distance between consecutive slices) is within
     * acceptable bounds: greater than MIN_SLICE_SPACING and less than MAX_SLICE_SPACING.
     */
    static checkSliceSpacing(group: DicomSeries): void {
        if (!group.isExcluded) {
            const sliceSpaces = DicomSeriesGeometryChecks.calculateSliceSpaces(group);

            if (sliceSpaces.length === 0) {
                DicomMessages.excludeWithErrorMessage(group, `Unable to calculate slice spaces`);
                return;
            }

            const minSpacing = Math.min(...sliceSpaces);
            const maxSpacing = Math.max(...sliceSpaces);

            if (minSpacing < MIN_SLICE_SPACING - SLICE_DISTANCE_TOLERANCE) {
                DicomMessages.excludeWithErrorMessage(
                    group,
                    `Slice spacing too small (${minSpacing.toFixed(3)}mm < ${MIN_SLICE_SPACING}mm)`,
                );
            }

            if (maxSpacing > MAX_SLICE_SPACING + SLICE_DISTANCE_TOLERANCE) {
                DicomMessages.excludeWithErrorMessage(
                    group,
                    `Slice spacing too large (${maxSpacing.toFixed(3)}mm > ${MAX_SLICE_SPACING}mm)`,
                );
            }
        }
    }

    /**
     * Calculate the spacing between consecutive slices.
     * Returns an array of distances between each pair of adjacent slices.
     */
    private static calculateSliceSpaces(group: DicomSeries): number[] {
        const nonExcludedDicoms = filterExcluded(group);

        const spaces: number[] = [];

        if (nonExcludedDicoms.length < 2) {
            return spaces;
        }

        for (let i = 0; i < nonExcludedDicoms.length - 1; i++) {
            const currentPosition = DicomInfoUtil.verticalImagePositionPatient(
                nonExcludedDicoms[i],
            );
            const nextPosition = DicomInfoUtil.verticalImagePositionPatient(
                nonExcludedDicoms[i + 1],
            );

            if (isFiniteNumber(currentPosition) && isFiniteNumber(nextPosition)) {
                spaces.push(Math.abs(currentPosition - nextPosition));
            } else {
                throw new Error('Invalid image position found when calculating slice space');
            }
        }

        return spaces;
    }

    /**
     * Check that slices are contiguous or overlapping by verifying that each slice
     * position plus half its thickness reaches or overlaps with the next slice position
     * minus half its thickness.
     *
     * Formula: currentPosition + (currentThickness / 2) >= nextPosition - (nextThickness / 2)
     */
    static checkSlicesAreContiguous(group: DicomSeries): void {
        if (!group.isExcluded) {
            const nonExcludedDicoms = filterExcluded(group);

            if (nonExcludedDicoms.length < 2) {
                throw new Error('Less than 2 non excluded slices left in scan');
            }

            for (let i = 0; i < nonExcludedDicoms.length - 1; i++) {
                const currentSlice = nonExcludedDicoms[i];
                const nextSlice = nonExcludedDicoms[i + 1];

                const currentPosition = DicomInfoUtil.verticalImagePositionPatient(currentSlice);
                const nextPosition = DicomInfoUtil.verticalImagePositionPatient(nextSlice);

                if (!isFiniteNumber(currentPosition) || !isFiniteNumber(nextPosition)) {
                    DicomMessages.excludeWithErrorMessage(
                        group,
                        `Invalid image positions found when checking contiguity`,
                    );
                    return;
                }

                const currentThickness = currentSlice.sliceThickness;
                const nextThickness = nextSlice.sliceThickness;

                if (!currentThickness || !nextThickness) {
                    DicomMessages.excludeWithErrorMessage(
                        group,
                        `Invalid slice thickness when checking contiguity`,
                    );
                    return;
                }

                // Check if slices are contiguous or overlapping
                // currentPosition + (currentThickness / 2) should be >= nextPosition - (nextThickness / 2)
                const currentEdge =
                    currentPosition - currentThickness / 2 - SLICE_DISTANCE_TOLERANCE;
                const nextEdge = nextPosition + nextThickness / 2 + SLICE_DISTANCE_TOLERANCE;

                // Check if there's still a gap even after applying tolerance
                if (currentEdge > nextEdge) {
                    const gap = nextEdge - currentEdge;
                    DicomMessages.excludeWithErrorMessage(
                        group,
                        `Slices are not contiguous: gap of ${gap.toFixed(3)}mm found between slices ${i} and ${i + 1}`,
                    );
                    return;
                }
            }
            log.info('All slices are contiguous or overlapping');
        }
    }

    /**
     * Check all images have slice thickness less than 3.0mm.
     * If any slice thickness is greater than 2.0mm, generate a warning.
     * Slices do not need to have the same thickness, just all within limits.
     */
    static checkSliceThickness(group: DicomSeries): void {
        const slicesWithThickness = filterExcluded(group).filter((i) => i.sliceThickness);

        if (slicesWithThickness.length === 0) {
            DicomMessages.excludeWithErrorMessage(
                group,
                `No slices with valid slice thickness found`,
            );
            return;
        }

        let hasWarning = false;
        let isExcluded = false;

        for (const slice of slicesWithThickness) {
            const thickness = slice.sliceThickness as number;

            if (thickness > MAX_THICKNESS) {
                isExcluded = true;
                break;
            }

            if (thickness > THICKNESS_WARNING) {
                hasWarning = true;
            }
        }

        if (isExcluded) {
            DicomMessages.excludeWithErrorMessage(
                group,
                `Images must have slice thickness less than 3.0mm`,
            );
        } else if (hasWarning) {
            DicomMessages.appendMessage(
                group.messages,
                DicomMessageLevel.Warning,
                `Some images have slice thickness greater than 2.0mm`,
            );
        }
    }

    /**
     * Sort the series by the third dimension of 'Image Position Patient' (0020,0032)
     *
     * This will sort them in reverse order (highest) 'z' value first, with invalid and
     * excluded items last.
     */
    static sortSeriesByImagePositionPatient(group: DicomSeries): void {
        function zItem(item: DicomInfo, defaultValue: number): number {
            if (Array.isArray(item.imagePositionPatient)) {
                const zValue = item.imagePositionPatient[2];
                return isFiniteNumber(zValue) ? zValue : defaultValue;
            }
            return defaultValue;
        }

        if (!group.isExcluded) {
            const nonPosition = -99999; // any value smaller than the minimum 'z' value
            group.items.sort((l: DicomInfo, r: DicomInfo) => {
                const lPosition: number = !l.isExcluded ? zItem(l, nonPosition) : nonPosition;
                const rPosition: number = !r.isExcluded ? zItem(r, nonPosition) : nonPosition;
                return rPosition - lPosition;
            });
        }
    }

    /**
     * Check that the scan covers at least 200 mm of vertical distance by calculating
     * the difference between the maximum and minimum image positions.
     */
    static checkScanCoverage(group: DicomSeries): void {
        if (!group.isExcluded) {
            const nonExcludedDicoms = filterExcluded(group);

            const positions = nonExcludedDicoms
                .map((dicomInfo) => DicomInfoUtil.verticalImagePositionPatient(dicomInfo))
                .filter((position) => isFiniteNumber(position)) as number[];

            if (positions.length === 0) {
                DicomMessages.excludeWithInfoMessage(
                    group,
                    `DICOM series has no valid image positions`,
                );
                return;
            }

            const minPosition = Math.min(...positions);
            const maxPosition = Math.max(...positions);
            const verticalCoverage = Math.abs(maxPosition - minPosition);

            if (verticalCoverage < MIN_SCAN_LENGTH) {
                DicomMessages.excludeWithInfoMessage(
                    group,
                    `DICOM series has insufficient linear coverage (${verticalCoverage.toFixed(1)}mm)`,
                );
            }
        }
    }

    /**
     * Check if two values are equal within a tolerance.
     */
    private static areEqualWithinTolerance(a: number, b: number, tolerance: number): boolean {
        return Math.abs(a - b) < tolerance;
    }
}
